import * as React from 'react';
import {Box, Checkbox, FormControlLabel, Typography} from "@mui/material";
import SteddiButton from "../General/SteddiButton";
import InitialPage from "./Steps/InitialPage";
import PriceInfoPage from "./Steps/PriceInfoPage";
import PaymentFreeMonthPage from "./Steps/PaymentFreeMonthPage";
import ConfirmPage from "./Steps/ConfirmPage";
import {getOrderInfo, startSigningProcess} from "../../api/SignRequests";
import environment from "../../environment";
import {useNavigate, useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import LoadingPage from "./Steps/LoadingPage";
import Grid2 from "@mui/material/Unstable_Grid2";
import useOrderState from "../../stores/orderStore";
import SignSteps from "../General/SignSteps";
import EnergiKontoPage from "./Steps/EnergiKontoPage";
import CalculationInfoPage from "./Steps/CalculationInfoPage";
import {ExternalLink} from "lucide-react";
import useAppState from "../../stores/appStateStore";
import PageHeader from "../General/PageHeader";
import NiceModal, {useModal} from "@ebay/nice-modal-react";
import CancelDialog from "./Steps/CancelDialog";
import useErrorStore from "../../stores/errorStore";
import {parseEndpointError} from "../../helpers/orderHelpers";
import {ReactComponent as SteddiGraph} from "../../images/Forutsigbar Betaling.svg"
import {ReactComponent as Calculator} from "../../images/Månedsbeløp.svg"
import {ReactComponent as Calculator2} from "../../images/Reberegning.svg"
import DateHelpers from "../../helpers/DateHelpers";
import StartSignDialog from "./StartSignDialog";
import {AxiosError} from "axios";

export default function SignWrapper() {
  const navigate = useNavigate()
  const orderState = useOrderState((state) => state)
  const error = useErrorStore((state) => state.error)
  const setError = useErrorStore((state) => state.setError)
  const editPaymentFreeMonths = useAppState((state) => state.editPaymentFreeMonths)
  const setEditPaymentFreeMonths = useAppState((state) => state.setEditPaymentFreeMonths)
  const goNextDisabled = useAppState((state) => state.goNextDisabled)
  const setGoNextDisabled = useAppState((state) => state.setGoNextDisabled)
  const goNextTooltip = useAppState((state) => state.goNextTooltip)
  const setRedirectBrand = useAppState((state) => state.setRedirectBrand)

  const [orderLoading, setOrderLoading] = useState(true)
  const [activeStep, setActiveStep] = React.useState(0);
  const [signProcessStarted, setSignProcessStarted] = React.useState(false);
  const [signError, setSignError] = React.useState(false);
  const [termsAccepted, setTermsAccepted] = React.useState(false);

  let {hashId} = useParams<string>()
  const modal = useModal(StartSignDialog);

  useEffect(() => {
    if (hashId) {
      orderState.setOrderHash(hashId)
      setOrderLoading(true)
      getOrderInfo(hashId!)
        .then(order => {
          orderState.setOrder(order)
          setRedirectBrand(order.brandId)
          const date = DateHelpers.getFirstPaymentDate()
          orderState.setFirstPaymentDue(date)
        })
        .catch(err => {
          setError(parseEndpointError(err))
        })
        .finally(() => setOrderLoading(false))
    }
  }, [hashId])

  useEffect(() => {
    if (editPaymentFreeMonths) {
      const index = steps.findIndex(step => step.title === "Betalingsfri måned")
      setActiveStep(index)
    }
  }, [editPaymentFreeMonths])

  useEffect(() => {
    if (error)
      navigate("error")
  }, [error])

  const handleNext = () => {
    resetEditPaymentFreeMonths()
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    window.scrollTo({top: 0, left: 0});
  };

  const handleBack = () => {
    resetEditPaymentFreeMonths()
    setGoNextDisabled(false)
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    window.scrollTo({top: 0, left: 0});
  };

  function handleCancel() {
    NiceModal.show(CancelDialog)
  }

  function handleStartSign() {
    if (hashId) {
      setSignProcessStarted(true)
      modal.show({error: signError})

      startSigningProcess(hashId, {paymentFreeMonths: orderState.paymentFreeMonths,})
        .then(res => {
          location.href = res
        })
        .catch((err: AxiosError) => {
          if (err?.response?.status === 418) {
            setSignError(true)
            modal.show({error: true})
          } else if (err) {
            setError(parseEndpointError(err))
            modal.hide()
          }
        })
    }
  }

  function resetEditPaymentFreeMonths() {
    if (editPaymentFreeMonths)
      setEditPaymentFreeMonths(false)
  }

  const steps = [
    {title: "Forutsigbar Betaling", element: <InitialPage/>, image: <SteddiGraph/>},
    {title: "Adresse", element: <EnergiKontoPage/>},
    {title: "Hva skal du betale?", element: <PriceInfoPage/>, image: <Calculator/>},
    {title: "Nyttig å vite om saldoen din", element: <CalculationInfoPage/>, image: <Calculator2/>},
    {title: "Betalingsfri måned", element: <PaymentFreeMonthPage/>},
    {title: "Fullfør bestillingen", element: <ConfirmPage/>},
  ]

  let isLastStep = activeStep === steps.length - 1;

  if (orderLoading) {
    return (
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <LoadingPage/>
      </Box>
    )
  }

  if (!orderLoading && orderState.totalEstimatedConsumption && orderState.totalEstimatedConsumption >= 50000 && activeStep == 3) {
    return (
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <Typography variant="h4" sx={{mt: 5}}>Beklager, bestilling ikke mulig</Typography>
        <Box sx={{mt: 3}}>
          <Typography variant="body1">
            Bestilling av Forutsigbar betaling forutsetter at du har et årlig strømforbruk som er lavere enn 50 000 kWh. Basert på informasjon vi har innhentet fra Elhub, er ditt forventede årlige strømforbruk {orderState.totalEstimatedConsumption} kWh.
          </Typography>
          <Typography variant="body1" mt={2}>
            På grunn av dette kan vi dessverre ikke tilby deg Forutsigbar betaling. Ta gjerne kontakt med Steddi kundeservice dersom du har noen spørsmål, du når oss på 55 90 73 50.
          </Typography>
        </Box>
      </Box>
    )
  }

  function getStepButtons() {
    if (activeStep === 0) {
      return (
        <Grid2 mt={5} container justifyContent="space-between" spacing={2}>
          <Grid2 xs={12} sm="auto">
            <a href="https://www.steddi.no/avtalevilkar" target="_blank">Avtalevilkår <ExternalLink size="15"/></a><br/>
            <a href="https://www.steddi.no/globalassets/dokumenter/angreskjema-og-skjema-for-opplysninger-om-angrerett-forutsigbar-betaling.pdf" target="_blank">Opplysninger om angrerett og angreskjema<ExternalLink size="15"/></a>
          </Grid2>
          <Grid2 mt={0} container justifyContent="space-between" spacing={2}>
          <Grid2 xs={12} sm="auto" sx={{display: {xs: "none", sm: "block"}}}>
            <SteddiButton variant="outlined" onClick={handleCancel} disabled={signProcessStarted}>Avbryt</SteddiButton>
          </Grid2>
          <Grid2 xs={12} sm="auto">
            <SteddiButton onClick={handleNext} disabled={signProcessStarted || goNextDisabled}>Kom i gang</SteddiButton>
          </Grid2>
          </Grid2>
        </Grid2>
      )
    } else {
      return (
        <Box mt={5}>
          {
            isLastStep && <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
              <FormControlLabel sx={{alignSelf: "flex-end"}} control={<Checkbox checked={termsAccepted} onChange={(a) => setTermsAccepted(a.target.checked)}/>} label={<span>Jeg aksepterer <a href="https://www.steddi.no/avtalevilkar" target="_blank">vilkårene for kredittavtalen</a> og er kjent med <a href="https://www.steddi.no/globalassets/dokumenter/angreskjema-og-skjema-for-opplysninger-om-angrerett-forutsigbar-betaling.pdf" target="_blank">opplysninger om angrerett og angreskjema</a></span>}/>
            </Box>
          }
          <Grid2 container spacing={2} justifyContent="space-between">
            <Grid2 xs={12} sm={"auto"}>
              <SteddiButton onClick={handleBack} color="secondary" disabled={signProcessStarted}>Forrige</SteddiButton>
            </Grid2>

            <Grid2 xs={12} sm="auto" container justifyContent="flex-end" alignItems="center" spacing={2}>

              <Grid2 xs={12} sm="auto" hidden={activeStep === 0} sx={{display: {xs: "none", sm: "block"}}}>
                <SteddiButton variant="outlined" onClick={handleCancel} disabled={signProcessStarted}>Avbryt</SteddiButton>
              </Grid2>
              <Grid2 xs={12} sm="auto">
                {isLastStep
                  ? <>
                    <div>

                      <SteddiButton onClick={handleStartSign} icon={<ExternalLink/>} disabled={signProcessStarted || !termsAccepted} loading={signProcessStarted}>Bestill og gå til signering</SteddiButton>
                    </div>
                  </>
                  : <SteddiButton onClick={handleNext} disabled={signProcessStarted || goNextDisabled} tooltip={goNextDisabled ? goNextTooltip : undefined}>Gå videre</SteddiButton>}
              </Grid2>

            </Grid2>
          </Grid2>
        </Box>
      )
    }
  }

  return (
    <>
      <Box pt={5}>
        <Box display="flex" justifyContent="flex-end">
          <SteddiButton variant="text" onClick={handleCancel} onlyShowXs noShrink sx={{mb: 3}} disabled={signProcessStarted}>Avbryt</SteddiButton>
        </Box>
        {steps[activeStep].image && <Box display="flex" alignItems="center" justifyContent="center" mb={5}>{steps[activeStep].image}</Box>}
        <PageHeader>{steps[activeStep].title}</PageHeader>
        <SignSteps activeStep={activeStep} amountOfSteps={steps.length + 1}/>
        {steps[activeStep].element}
      </Box>
      {getStepButtons()}
    </>
  );
}